// src/lib/humanTimeAgo.ts
// Utility to format a past date as a human-friendly "time ago" string for Vue 2 apps.
// Only uses built-in Date APIs.
// Designed to complement (not duplicate) the exact date/time shown separately.
/**
 * Returns a human-readable string describing how long ago the given date was.
 * Includes weekday names for better context. Avoids duplicating date/time info.
 * @param date Date object or ISO string
 */
export function humanTimeAgo(date) {
    const now = new Date();
    const d = typeof date === 'string' ? new Date(date) : date;
    const diffMs = now.getTime() - d.getTime();
    if (isNaN(d.getTime()) || diffMs < 0)
        return 'in the future';
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    // Weekday helpers
    const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayNamesFull = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const getShortDay = (date) => dayNamesShort[date.getDay()];
    const getFullDay = (date) => dayNamesFull[date.getDay()];
    // Time of day helper (morning/afternoon/evening/night)
    const getTimeOfDay = (date) => {
        const hour = date.getHours();
        if (hour < 6)
            return 'night';
        if (hour < 12)
            return 'morning';
        if (hour < 17)
            return 'afternoon';
        if (hour < 21)
            return 'evening';
        return 'night';
    };
    // Today boundaries
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    // Just now / minutes ago - more granular
    if (seconds < 60)
        return 'just now';
    if (minutes < 5)
        return 'moments ago';
    if (minutes < 15)
        return 'few min ago';
    if (minutes < 30)
        return `${minutes} min ago`;
    if (minutes < 45)
        return 'half hour ago';
    if (minutes < 60)
        return `${minutes} min ago`;
    // Hours ago (< 3 hours)
    if (hours < 3)
        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    // Same day - show time of day
    if (d >= today) {
        const timeOfDay = getTimeOfDay(d);
        if (hours < 6)
            return 'earlier today';
        return `this ${timeOfDay}`;
    }
    // Yesterday - show time of day
    if (d >= yesterday) {
        const timeOfDay = getTimeOfDay(d);
        return `yesterday ${timeOfDay}`;
    }
    // 2-6 days ago: "Mon morning" / "Tue evening"
    if (days >= 2 && days <= 6) {
        const timeOfDay = getTimeOfDay(d);
        return `${getShortDay(d)} ${timeOfDay}`;
    }
    // 7-13 days ago: "last Monday evening"
    if (days >= 7 && days <= 13) {
        const timeOfDay = getTimeOfDay(d);
        return `last ${getFullDay(d)} ${timeOfDay}`;
    }
    // 14-30 days: "2 weeks ago on Wed"
    if (days >= 14 && days <= 30) {
        const weeks = Math.floor(days / 7);
        return `${weeks} week${weeks === 1 ? '' : 's'} ago on ${getShortDay(d)}`;
    }
    // 31-365 days: "3 months ago on Friday"
    if (days >= 31 && days <= 365) {
        const months = Math.floor(days / 30.44);
        if (months === 1) {
            return `1 month ago on ${getFullDay(d)}`;
        }
        return `${months} months ago on ${getFullDay(d)}`;
    }
    // Over a year: "2 years ago on Sunday"
    const years = Math.floor(days / 365.25);
    if (years === 1) {
        return `1 year ago on ${getFullDay(d)}`;
    }
    return `${years} years ago on ${getFullDay(d)}`;
}
